import * as glMatrix from 'gl-matrix';
import { GLTexture } from './texture.js';
import { Camera } from './scene/camera.js';
import { Tool } from './operations/tools/tool.js';
import { GLEngineResources } from './engineResources.js';
import { StampStroke } from './scene/stroke/stamp.js';
import { Filter } from './operations/filters/filter.js';

export class Layer {
    _resources: GLEngineResources;
    _image: any;
    _alpha: any;
    _texture: GLTexture;
    _alphaTexture: GLTexture | null;
    _result: GLTexture;
    _activeOperation: any | null;
    _operations: any[];
    _sortedOperations: any[];
    _camera: Camera;
    _rotation: glMatrix.vec3;
    _scaledHeight: any;
    _scaledWidth: number;
    width: any;
    height: any;

    constructor(resources, image, alpha) {
        var self = this;
        self._resources = resources;
        self._image = image;
        self._alpha = alpha;
        self._texture = GLTexture.createTexture2d(self._resources.gl, false);
        self._texture.setPixels(image);

        if (alpha) {
            self._alphaTexture = GLTexture.createTexture2d(self._resources.gl, true);
            self._alphaTexture.setPixels(alpha);
        } else {
            self._alphaTexture = null;
        }

        self._result = GLTexture.createTexture2d(self._resources.gl, false);

        self._activeOperation = null;
        self._operations = [];
        self._sortedOperations = [];

        self._camera = new Camera(true);

        self._rotation = glMatrix.vec3.create();

        if (self._resources.width > self._resources.height) {
            self._scaledHeight = self._resources.height;
            self._scaledWidth = self._scaledHeight * self.aspect;
        } else {
            self._scaledWidth = self._resources.width;
            self._scaledHeight = self._scaledWidth / self.aspect;
        }
        self.width = self._scaledWidth;
        self.height = self._scaledHeight;
    }

    get name() {
        var decoded = decodeURI(this._image.src);
        return decoded.replace(/^.*[\\\/]/, '');
    }

    get texture() {
        return this._texture;
    }

    get alpha() {
        return this._alphaTexture;
    }

    get image() {
        return this._image;
    }

    get aspect() {
        return this._image.naturalWidth / this._image.naturalHeight;
    }

    get result() {
        return this._result;
    }

    getRotation(axe) {
        var self = this;
        switch (axe) {
            case 'X':
                return self._rotation[0];
            case 'Y':
                return self._rotation[1];
            case 'Z':
                return self._rotation[2];
        }
    }

    setRotation(axe, value) {
        var self = this;
        switch (axe) {
            case 'X':
                self._rotation[0] = value;
                break;
            case 'Y':
                self._rotation[1] = value;
                break;
            case 'Z':
                self._rotation[2] = value;
                break;
        }
    }

    get camera() {
        return this._camera;
    }

    get model() {
        var self = this;
        var model = glMatrix.mat4.create();
        glMatrix.mat4.rotateX(model, model, self._rotation[0]);
        glMatrix.mat4.rotateY(model, model, -self._rotation[1]);
        glMatrix.mat4.rotateZ(model, model, self._rotation[2]);
        glMatrix.mat4.scale(model, model, glMatrix.vec3.fromValues(self.aspect, 1, 1));
        return model;
    }

    get enable() {
        return this._texture.enable;
    }

    set enable(value) {
        this._texture.enable = value;
    }

    touch() {
        var self = this;
        for (var i = 0; i < self._operations.length; ++i) {
            self._operations[i].touch();
        }
    }

    get operations() {
        return this._operations;
    }

    addOperation(value) {
        var self = this;
        if (!value || !self.enable) {
            return;
        }

        var index = self._operations.indexOf(value);
        if (index < 0) {
            self._operations.push(value);
            self._sortOperations();
        } else {
            for (var i = index; i < self._operations.length; ++i) {
                self._operations[i].touch();
            }
            self._operations[index] = value;
        }
    }

    deleteOperation(value) {
        var self = this;
        var index = self._operations.indexOf(value);
        if (index >= 0) {
            for (var i = index; i < self._operations.length; ++i) {
                self._operations[i].touch();
            }
            self._operations[index].destroy();
            self._operations.splice(index, 1);
            self._sortOperations();
        }
    }

    get activeOperation() {
        return this._activeOperation;
    }

    set activeOperation(value) {
        this._activeOperation = value;
    }

    get hasToolOperation() {
        var self = this;
        if (self._sortedOperations.length === 0) {
            return false;
        }

        if (self._sortedOperations[0] instanceof Tool) {
            return true;
        }

        return false;
    }

    render(selection, disableFiters) {
        var self = this;
        var gl = self._resources.gl;
        var current_texture = self.enable ? self._result : null;

        var touched = false;
        var operations = self._sortedOperations;
        if (disableFiters) {
            operations = operations.filter(function (value) {
                if (value instanceof Filter) {
                    value.touch();
                    return false;
                }
                return true;
            });
        }

        if (current_texture) {
            self._blit(current_texture);

            for (var i = 0; i < operations.length && current_texture; ++i) {
                var operation = operations[i];
                touched = (touched || operation.touched());
                if (!operation.activated || operation.disabled) {
                    continue;
                }

                if (touched) {
                    operation.touch();
                    var previous_active_operation_index = self._previousActiveOperationIndex(i, operations);
                    if (previous_active_operation_index != i) {
                        current_texture = operations[previous_active_operation_index].texture;
                    }
                }
                if (!touched) {
                    if (self._canBeDestroyed(i, operations)) {
                        operation.destroy();
                    } else {
                        current_texture = operation.texture;
                        self._blit(current_texture);
                    }
                    continue;
                }

                if (operation instanceof Filter) {
                    current_texture = self._renderFilter(selection, operation, current_texture);
                } else if (operation instanceof Tool) {
                    current_texture = self._renderTool(selection, operation, current_texture);
                }

                operation.untouch();
                gl.finish();
            }

            //draw active filter
            if (self._activeOperation instanceof Filter) {
                if (self.activeOperation.activated && self._operations.indexOf(self.activeOperation) < 0 && current_texture) {
                    self.activeOperation.touch();
                    current_texture = self._renderFilter(selection, self.activeOperation, current_texture);
                }
            }
        }
        return touched;
    }

    _blit(current_texture) {
        var self = this;
        var gl = self._resources.gl;

        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        var program = self._resources.programs['blit'];
        program.use();
        gl.activeTexture(gl.TEXTURE0);
        current_texture.bind();
        gl.uniform1i(program.uniformLocation('texture'), 0);
        self._resources.meshes['unit'].bind(program);
        self._resources.meshes['unit'].draw();
        self._resources.meshes['unit'].unbind(program);
        current_texture.unbind();
        program.unuse();
    }

    _renderStroke(selection, operation, current_texture) {
        var self = this;
        var stroke = operation.stroke;
        if (!stroke) {
            return;
        }

        var gl = self._resources.gl;
        if (stroke && stroke.hasPoint) {
            //init framebuffer with previous mask
            if (stroke.accumulate) {
                self._blit(stroke);
            } else {
                self._blit(current_texture);
                stroke.bind();
                stroke.store();
                stroke.unbind();
            }

            var program = self._resources.programs[stroke.program];
            program.use();
            program.setUniforms(operation);
            var scale = [operation.size / self._resources.width, operation.size / self._resources.height, 1];
            var quat = glMatrix.quat.create();
            //render stroke mask
            while (stroke.hasPoint) {
                stroke.setUniforms(program);
                var position = stroke.point;
                var position_NDC = glMatrix.vec3.fromValues(position[0], position[1], 0);
                glMatrix.mat4.fromRotationTranslationScale(self._resources.meshes['stoke'].model, quat, position_NDC, new Float32Array(scale));
                gl.activeTexture(gl.TEXTURE0);
                stroke.bind();
                gl.uniform1i(program.uniformLocation('texture'), 0);
                if (stroke instanceof StampStroke) {
                    gl.activeTexture(gl.TEXTURE1);
                    current_texture.bind();
                    gl.uniform1i(program.uniformLocation('current_texture'), 1);
                }
                gl.uniform1i(program.uniformLocation('use_selection'), selection.hasSelection);
                if (selection.hasSelection) {
                    gl.activeTexture(gl.TEXTURE2);
                    selection.target.bind();
                    gl.uniform1i(program.uniformLocation('selection_mask'), 2);
                }
                gl.uniformMatrix4fv(program.uniformLocation('model'), false, self._resources.meshes['stoke'].model);
                self._resources.meshes['stoke'].bind(program);
                self._resources.meshes['stoke'].draw();
                self._resources.meshes['stoke'].unbind(program);
                if (stroke instanceof StampStroke) {
                    current_texture.unbind();
                }
                if (selection.activated) {
                    selection.target.unbind();
                }
                stroke.unbind();
                stroke.store();
                stroke.next();
            }
            program.unuse();
        }
    }

    _renderTool(selection, operation, current_texture) {
        var self = this;
        if (!operation.touched()) {
            return operation.texture;
        }

        var gl = self._resources.gl;
        var program = self._resources.programs[operation.program];
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        self._renderStroke(selection, operation, current_texture);
        program.use();
        program.setUniforms(operation);
        gl.activeTexture(gl.TEXTURE0);
        current_texture.bind();
        gl.uniform1i(program.uniformLocation('texture'), 0);
        if (operation.stroke) {
            gl.activeTexture(gl.TEXTURE1);
            operation.stroke.bind();
            gl.uniform1i(program.uniformLocation('stroke_mask'), 1);
        }
        self._resources.meshes['layer'].bind(program);
        self._resources.meshes['layer'].draw();
        self._resources.meshes['layer'].unbind(program);
        current_texture.unbind();
        if (operation.stroke) {
            operation.stroke.unbind();
        }
        program.unuse();
        operation.store();
        return operation.texture;
    }

    _renderFilter(selection, operation, current_texture) {
        var self = this;
        if (!operation.touched()) {
            return operation.texture;
        }

        if (selection.hasSelection && !operation.hasSelection) {
            operation.storeSelection(selection.framebuffer);
        }

        var gl = self._resources.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);
        gl.clearColor(0.0, 0.0, 0.0, 0.0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        var program = self._resources.programs[operation.program];
        program.use();
        program.setUniforms(operation);
        gl.activeTexture(gl.TEXTURE0);
        current_texture.bind();
        gl.uniform1i(program.uniformLocation('texture'), 0);
        gl.uniform1i(program.uniformLocation('use_selection'), operation.hasSelection);
        if (operation.hasSelection) {
            gl.activeTexture(gl.TEXTURE1);
            operation.selection.bind();
            gl.uniform1i(program.uniformLocation('selection_mask'), 1);
        }
        self._resources.meshes['layer'].bind(program);
        self._resources.meshes['layer'].draw();
        self._resources.meshes['layer'].unbind(program);
        current_texture.unbind();
        if (operation.hasSelection) {
            operation.selection.unbind();
        }
        program.unuse();
        operation.store();
        return operation.texture;
    }

    store() {
        var self = this;
        var gl = self._resources.gl;
        gl.bindFramebuffer(gl.FRAMEBUFFER, self._resources.framebuffer);
        gl.viewport(0, 0, self._resources.width, self._resources.height);
        self._result.bind();
        gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 0, 0, self._resources.width, self._resources.height, 0);
        self._result.unbind();
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    }

    destroy() {
        var self = this;
        if (self._texture) {
            self._texture.destroy();
        }

        for (var i = 0; i < self._operations.length; ++i) {
            var operation = self._operations[i];
            operation.destroy();
        }
    }

    _sortOperations() {
        var self = this;
        self._sortedOperations = self._operations.slice().sort(function (lhs, rhs) {
            if (lhs instanceof Tool && rhs instanceof Filter) {
                return -1;
            }

            if (lhs instanceof Filter && rhs instanceof Tool) {
                return 1;
            }

            return 0;
        });
    }

    _previousActiveOperationIndex(index, operations) {
        for (var i = index - 1; i >= 0; --i) {
            if (operations[i].activated) {
                return i;
            }
        }

        return index;
    }

    _canBeDestroyed(index, operations) {
        var self = this;
        var active_operation_index = operations.indexOf(self.activeOperation);
        if (active_operation_index != -1) {
            if (index >= active_operation_index) {
                return false;
            }

            var previous_active_operation_index = self._previousActiveOperationIndex(active_operation_index, operations);
            if (index == previous_active_operation_index) {
                return false;
            }
        }

        var last_active_operation_index = self._previousActiveOperationIndex(operations.length, operations);
        if (index >= last_active_operation_index - 2) {
            return false;
        }

        return true;
    }

}